import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '@env/environment';
import {
  myTrends,
  outofExpense,
  reportDownloadRequest,
} from '@interface/reportDownload';
import { ReportDownloadService } from '@core/services/ReportDownload/report-download.service';
import { GlobalService } from '@core/services/global/global.service';
import {
  combineLatest,
  distinctUntilChanged,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { GroupMember } from '@interface/Dealer';
import { CustomDropdownComponent } from '@shared/components/custom-dropdown/custom-dropdown.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MonthValue } from '../../enums/Date';

@Component({
  selector: 'app-report-download',
  standalone: true,
  imports: [
    CommonModule,
    CustomDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './report-download.component.html',
  styleUrl: './report-download.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class ReportDownloadComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  readonly MAX_YEARS: number = 5;

  reportData: any;
  categories = ['Dealer', 'Classic', 'Variable', 'Fixed', 'DGM'];
  myTrendsColumns = ['Dealer', 'Variable', 'Fixed'];
  finalComposites: any = {};
  myTrends: any = {};
  month: any;
  year: any;
  selectedDealer: GroupMember | null = null;
  groupId: number | null = null;
  expenseAbsorption: any = {};
  expenseReductionReports: any = {};
  grossPotentials: any = {};

  outOfExpenseTables: outofExpense[] = [];
  dropDownData: any[] = [];
  selectedMonthYear: any;
  selectedDropDownValue: any;
  showModalSpinner: boolean = false;
  subscription?: Subscription;
  myTrendsMap: { [key: string]: myTrends } = {};
  groupCompositeReports: any[] = [];
  outOfExpenseTableData: { columns: string[]; rows: any[] } | any = {
    columns: [],
    rows: [],
  };
  outColumns: string[] = [];
  outRows: any[] = [];
  exprrAllow: boolean = false;

  constructor(
    private readonly reportDownloadService: ReportDownloadService,
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.globalService.setActiveComponent('Report-Download');
    this.globalService.updateShowOverlaySpinner(true);
    this.cdr.detectChanges();
    setTimeout(() => {
      const dealersPromise = this.selectedDealer
        ? Promise.resolve()
        : this.getDealersData();
      dealersPromise
        .then(() => {
          this.subscribeToGroupIdDealerCode();
        })
        .catch((e) => {
          console.error('Error fetching Report Download Dropdown Data:', e);
        })
        .finally(() => {
          setTimeout(() => {
            this.globalService.updateShowOverlaySpinner(false);
            this.cdr.detectChanges();
          }, 2000);
        });
    }, 0);
  }

  ngOnDestroy(): void {
    this.globalService.setActiveComponent(null);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async getDealersData() {
    const currentDate = new Date();
    let currentMonth = currentDate.getMonth();
    let currentYear = currentDate.getFullYear();

    if (currentMonth === 1) {
      currentMonth = 12;
      currentYear -= 1;
    }

    const url = `${environment.getOrgDetailsByGroupCode}?strmt_month=${currentMonth}&stmt_year=${currentYear}`;
    try {
      const data: any = await this.globalService.getApiDataWithReturn(url);
      if (!data?.success) {
        console.error(data?.message);
        return;
      }
      this.globalService.updateAllDealers(data.data);
      this.globalService.setDefaultDealer(data.data);
    } catch (error) {
      console.log(error);
    }
  }

  subscribeToGroupIdDealerCode(): void {
    if (
      !this.globalService.selectedDealer$ ||
      !this.globalService.activeComponent$
    ) {
      return;
    }

    combineLatest([
      this.globalService.selectedDealer$.pipe(distinctUntilChanged()),
      this.globalService.activeComponent$.pipe(distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async ([dealer, activeComponent]) => {
        this.selectedDealer = dealer;
        if (
          activeComponent === 'Report-Download' &&
          this.selectedDealer?.groupMemberCode
        ) {
          await this.getDropdownData();
          this.getReportsDownload();
        }
      });
  }

  async getDropdownData() {
    if (!this.selectedDealer?.groupMemberCode) {
      console.warn('No groupMemberCode');
      return;
    }
    try {
      const response = await this.globalService.getDynamicDates(this.MAX_YEARS);
      if (response.statusCode === 200 && response.data) {
        this.dropDownData = response.data.data.groupComposites;
        this.selectedDropDownValue = this.dropDownData[0] || null;
      } else {
        console.error('Failed to fetch dropdown data:', response);
        this.dropDownData = [];
        this.selectedDropDownValue = null;
      }
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
      this.dropDownData = [];
      this.selectedDropDownValue = null;
    }
    this.cdr.detectChanges();
  }

  onMonthYearChange(selectedValue: any) {
    this.selectedDropDownValue = selectedValue;
    this.getReportsDownload();
  }

  public async getReportsDownload(): Promise<void> {
    const reportDownloadRequest = this.reportDownloadRequest();
    if (!reportDownloadRequest) {
      return;
    }
    this.globalService.updateShowOverlaySpinner(true);
    this.cdr.detectChanges();
    this.showModalSpinner = true;
    try {
      const isLocalhost = window.location.hostname === 'localhost';
      let url = `${environment.getReportDownloadData}?month=${reportDownloadRequest?.month}&year=${reportDownloadRequest?.year}&groupcompositeid=${reportDownloadRequest?.groupCompositeId}`;
      if (isLocalhost) {
        url += `&code=${environment.functionappCode}`;
      }
      const response = await this.reportDownloadService.reportDownload(url);
      this.showModalSpinner = false;
      this.updateReportData(response);
      this.globalService.updateShowOverlaySpinner(false);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error adding custom Group:', error);
    } finally {
      this.globalService.updateShowOverlaySpinner(false);
      this.showModalSpinner = false;
      this.cdr.detectChanges();
    }
  }

  public reportDownloadRequest(): reportDownloadRequest | null {
    const monthYearObj = this.selectedDropDownValue;
    if (!monthYearObj?.monthYear || !this.selectedDealer?.groupMemberCode) {
      return null;
    }
    const [monthName, yearStr] = monthYearObj.monthYear.split(' ');
    const monthString = MonthValue[monthName as keyof typeof MonthValue];
    const month = parseInt(monthString, 10);
    const year = parseInt(yearStr, 10);
    if (!month || isNaN(year)) {
      return null;
    }
    this.month = month;
    this.year = year;
    return {
      groupmembercode: this.selectedDealer?.groupMemberCode,
      month: this.month,
      year: this.year,
      groupCompositeId: monthYearObj.groupCompositeId,
    };
  }

  updateReportData(response: any): void {
    if (!response) return;

    this.reportData = response;
    const output = response?.reportDownloadOutput;

    this.expenseAbsorption = output?.dealerDownloads?.expenseAbsorption || {};
    this.grossPotentials = output?.dealerDownloads?.grossPotentials || {};
    this.expenseReductionReports =
      output?.dealerDownloads?.expenseReductionReports || {};

    this.groupCompositeReports =
      output?.groupDownloads?.compositesReports || [];

    const out = output?.dealerDownloads?.OutOfExpenses || {};
    this.outColumns = out?.columns || [];
    this.outRows = out?.rows || [];
    this.exprrAllow = out?.allowAccess || false;

    if (Array.isArray(output?.outOfExpenseTables)) {
      this.outOfExpenseTables = output.outOfExpenseTables;
    } else {
      this.outOfExpenseTables = [];
    }

    const finalCompositesArray = output?.dealerDownloads?.finalComposites || [];

    this.finalComposites = {};

    this.categories.forEach((category) => {
      const match = finalCompositesArray.find(
        (item: any) => item.bookType === category,
      );

      this.finalComposites[category] = {
        pdf: match?.pdf?.exists ? match?.pdf?.downloadUrl : null,
        excel: match?.xlsx?.exists ? match?.xlsx?.downloadUrl : null,
        pdfReportPath: match?.pdf?.label || null,
        excelReportPath: match?.xlsx?.label || null,
        allowAccessPdf: match?.pdf?.allowAccess || false,
        allowAccessXl: match?.xlsx?.allowAccess || false,
      };
    });

    const myTrendArr = output?.dealerDownloads?.myTrends || [];
    this.myTrends = {};

    this.categories.forEach((category) => {
      const match = myTrendArr.find((r: any) =>
        r?.label?.toLowerCase().includes(category.toLowerCase()),
      );

      this.myTrends[category] = {
        exists: match?.exists || false,
        xmlReport: match?.exists ? match?.downloadUrl : null,
        report: match?.label || null,
        allowAccess: match?.allowAccess || false,
      };
    });
    this.expenseAbsorption = {
      pdfexists:
        output?.dealerDownloads?.expenseAbsorption?.pdf?.exists || false,
      pdfDownloadUrl:
        output?.dealerDownloads?.expenseAbsorption?.pdf?.downloadUrl || null,
      xlsexists:
        output?.dealerDownloads?.expenseAbsorption?.xlsx?.exists || false,
      xlsDownloadUrl:
        output?.dealerDownloads?.expenseAbsorption?.xlsx?.downloadUrl || null,
      allowAccessXl:
        output?.dealerDownloads?.expenseAbsorption?.xlsx?.allowAccess || false,
      allowAccessPdf:
        output?.dealerDownloads?.expenseAbsorption?.pdf?.allowAccess || false,
    };

    this.grossPotentials = {
      pdfexists: output?.dealerDownloads?.grossPotentials?.pdf?.exists || false,
      pdfDownloadUrl:
        output?.dealerDownloads?.grossPotentials?.pdf?.downloadUrl || null,
      xlsexists:
        output?.dealerDownloads?.grossPotentials?.xlsx?.exists || false,
      xlsDownloadUrl:
        output?.dealerDownloads?.grossPotentials?.xlsx?.downloadUrl || null,
      allowAccessXl:
        output?.dealerDownloads?.grossPotentials?.xlsx?.allowAccess || false,
      allowAccessPdf:
        output?.dealerDownloads?.grossPotentials?.pdf?.allowAccess || false,
    };

    this.expenseReductionReports = {
      pdfexists:
        output?.dealerDownloads?.expenseReductionReports?.pdf?.exists || false,
      pdfDownloadUrl:
        output?.dealerDownloads?.expenseReductionReports?.pdf?.downloadUrl ||
        null,
      xlsexists:
        output?.dealerDownloads?.expenseReductionReports?.xlsx?.exists || false,
      xlsDownloadUrl:
        output?.dealerDownloads?.expenseReductionReports?.xlsx?.downloadUrl ||
        null,
      allowAccessXl:
        output?.dealerDownloads?.expenseReductionReports?.xlsx?.allowAccess ||
        false,
      allowAccessPdf:
        output?.dealerDownloads?.expenseReductionReports?.pdf?.allowAccess ||
        false,
    };

    this.cdr.detectChanges();
  }
}
